import "pixi";
import "phaser";

import { FLOAT_EPSILON } from "./constants";
import { Slope, Line, Ray, LineSide, signOf } from "./collision";
import * as collision from "./collision";

export type TileDescriptor = string | {
    sprite: string,
    offset?: { x?: number, y?: number },
}

export type TileablePlatformAsset = {
    left: string | TileDescriptor,
    middle: string[],
    right: string | TileDescriptor,
    offset?: { x?: number, y?: number },
    dimensions?: { width?: number, height?: number },
}[];


export type AssetStyle = TileablePlatformAsset;
export type AssetStyleCollection = { [key: string]: AssetStyle };


export class TintFilter extends Phaser.Filter {
    constructor(game: Phaser.Game, r: number, g: number, b: number, a: number) {
        super(game, {}, "");

        this.fragmentSrc = [`
precision mediump float;
varying vec2 vTextureCoord;
varying vec4 vColor;

uniform sampler2D uSampler;

vec4 tintColor = vec4(${r}, ${g}, ${b}, ${a});

void main(void) {
    vec4 ownColor = texture2D(uSampler, vTextureCoord);
    gl_FragColor = vec4((0.1 * tintColor.rgb + (0.9 * tintColor * ownColor).rgb) * ownColor.a, ownColor.a);
}`];
    }
}


interface PlatformAttributes {
    spiked: boolean;
}

function defaultAttrs() {
    return {
        spiked: false,
    };
};

export abstract class Platform {
    attrs: PlatformAttributes;
    name: string;

    constructor(attrs: PlatformAttributes) {
        this.attrs = attrs;
    }

    get displayObject(): PIXI.DisplayObject {
        throw "Not implemented";
    }

    get physicsBody(): Phaser.Physics.P2.Body {
        throw "Not implemented";
    }

    translate(dx: number, dy: number) {
    }

    constrainTranslation(
        offsetX: number, offsetY: number,
        dx: number, dy: number,
        bbox: collision.AABBLike,
    ): [number, collision.ConstraintDirection[]] {
        throw "Not implemented";
    }

    intersects(line: Line): boolean {
        return false;
    }

    intersectionTime(line: Line): number | null {
        return null;
    }

    whichSide(line: Line): LineSide {
        throw "Not implemented";
    }

    highlightIntersection(highlight: boolean) {
    }

    serialize(): any {
        throw "Not implemented";
    }

    serializeMutable(): any {
        throw "Not implemented";
    }

    deserializeMutable(json: any, levelScale: number = 1): void {
        throw "Not implemented";
    }
}

export abstract class TiledPlatform extends Platform {
    platform: Phaser.Graphics;
    container: Phaser.Group;
    highlightFilter: Phaser.Filter;
    baseFilter: null | Phaser.Filter[];
    intersectionHighlighted: boolean;

    get displayObject(): PIXI.DisplayObject {
        return this.container;
    }

    get physicsBody(): Phaser.Physics.P2.Body {
        return this.platform.body;
    }

    highlightIntersection(highlight: boolean) {
        if (highlight && !this.intersectionHighlighted) {
            this.intersectionHighlighted = highlight;
            this.redraw();
        }
        else if (!highlight && this.intersectionHighlighted) {
            this.intersectionHighlighted = highlight;
            this.redraw();
        }
    }

    redraw() {
        if (this.intersectionHighlighted) {
            this.container.filters = [this.highlightFilter];
        }
        else {
            (this.container as any).filters = this.baseFilter;
        }
    }

    protected setup(
        game: Phaser.Game, asset: TileablePlatformAsset,
        x: number, y: number,
        totalWidth: number, totalHeight: number
    ): [number, number] {
        this.intersectionHighlighted = false;
        this.baseFilter = null;

        let platform = this.platform = game.add.graphics(x, y);

        let group = this.container = game.add.group();
        this.highlightFilter = new TintFilter(game, 3.0, 0.0, 0.0, 0.5);

        let currentHeight = 0;
        let realHeight = 0;
        let rowNumber = 0;

        const textureFor = function(tile: TileDescriptor) {
            if (typeof tile === "string") {
                return tile;
            }
            return tile.sprite;
        };

        while (currentHeight < totalHeight) {
            const assetRow = currentHeight == 0 ? asset[0] :
                asset[1 + (rowNumber - 1) % (asset.length - 1)];
            const yOffset = currentHeight;

            const tileHeight =
                (assetRow.dimensions && assetRow.dimensions.height) ?
                assetRow.dimensions.height :
                game.cache.getImage(assetRow.middle[0]).height;
            realHeight += tileHeight;
            if (assetRow.offset && assetRow.offset.y) {
                realHeight += assetRow.offset.y;
            }

            let currentWidth = 0;
            let middleOffset = 0;
            const rightTexture = game.cache.getImage(textureFor(assetRow.right));
            while (currentWidth < totalWidth) {
                let tile: TileDescriptor = assetRow.middle[0];
                if (currentWidth == 0) {
                    tile = assetRow.left;
                }
                else if (currentWidth + rightTexture.width >= totalWidth) {
                    tile = assetRow.right;
                }
                else {
                    tile = assetRow.middle[middleOffset];
                    middleOffset = (middleOffset + 1) % assetRow.middle.length;
                }

                const tileSprite = typeof tile === "string" ? tile : tile.sprite;
                const tileYOffset = typeof tile !== "string" && tile.offset && tile.offset.y ? tile.offset.y : 0;
                const xOffset = currentWidth;
                const tileWidth = game.cache.getImage(tileSprite).width;
                currentWidth += tileWidth;
                game.add.sprite(xOffset, yOffset + tileYOffset, tileSprite, null, group);
            }
            currentHeight += game.cache.getImage(assetRow.middle[0]).height;
            rowNumber++;
        }

        game.physics.p2.enable(platform);
        platform.body.static = true;
        return [totalWidth, realHeight];
    }
}

export class FiniteImmobilePlatform extends TiledPlatform {
    width: number;
    height: number;
    assetName: string;


    constructor(game: Phaser.Game, json: any, assetStyles: AssetStyleCollection, levelScale: number = 1) {
        super(json.attributes || defaultAttrs());

        let x = json.x * levelScale, y = json.y * levelScale;
        let width = json.width * levelScale, height = json.height * levelScale;

        let [_, realHeight] = this.setup(game, assetStyles[json.display.assetName], x, y, width, height);
        // this is a hack right now, this will change when we have real sprites
        // tighten bounds for temple
        // TODO: tighten bounds for shrine as well
        this.platform.body.clearShapes();
        if (json.display.assetName == "temple"){
            this.platform.body.addRectangle(width, realHeight-20, width/2, height  - realHeight/2 + 10);
            this.platform.body.addRectangle(width-32, 21, width/2, height - realHeight + 11);
        }
        else {
            this.platform.body.addRectangle(width, realHeight, width/2, height - realHeight/2);
        }

        this.name = json.message;
        this.assetName = json.display.assetName;
        this.width = width;
        this.height = height;
        this.redraw();
    }

    redraw() {
        super.redraw();
        this.container.position.x = this.platform.position.x;
        this.container.position.y = this.platform.position.y;
    }

    translate(dx: number, dy: number) {
        this.setPosition(this.platform.x + dx, this.platform.y + dy);
    }

    setPosition(x: number, y: number) {
        this.platform.x = x;
        this.platform.y = y;
        this.platform.body.reset(this.platform.x, this.platform.y);
        this.redraw();
    }

    constrainTranslation(
        offsetX: number, offsetY: number,
        dx: number, dy: number,
        bbox: collision.AABBLike,
    ): [number, collision.ConstraintDirection[]] {
        return collision.constrainAABBTranslation(
            { x: this.platform.x + offsetX, y: this.platform.y + offsetY,
              width: this.width, height: this.height },
            dx, dy, bbox);
    }

    get aabb(): collision.AABBLike {
        return { x: this.platform.x, y: this.platform.y,
                 width: this.width, height: this.height };
    }

    intersects(line: Line): boolean {
        return line.intersectsAABB(this.aabb);
    }

    intersectionTime(line: Line): number | null {
        return line.intersectsAABBTime(this.aabb);
    }

    whichSide(line: Line): LineSide {
        return line.whichSide(this.platform);
    }

    serialize(): any {
        return {
            type: "FiniteImmobilePlatform",
            x: this.platform.x,
            y: this.platform.y,
            width: this.width,
            height: this.height,
            display: {
                assetName: this.assetName,
            },
        };
    }

    serializeMutable(): any {
        return {
            x: this.platform.x,
            y: this.platform.y,
        };
    }

    deserializeMutable(json: any, levelScale: number = 1): void {
        this.setPosition(json.x * levelScale, json.y * levelScale);
    }
}



// WEBPACK FOOTER //
// ./src/platform.ts